'use client'

import { Loader2, Mic } from 'lucide-react'
import * as React from 'react'

import { toast } from '@/hooks/use-toast'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

import uploadService from '@/services/upload.service'
import {
  buildAudioPhraseHtml,
  buildSectionAudioHtml,
  MarkdownAudioAttachmentType,
} from '@/utils/audio-phrase-markdown'
import { ErrorResponse } from '@/utils/axios-error-handle'

interface MarkdownAudioPhraseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedText: string
  hasSelection: boolean
  onInsert: (result: { type: MarkdownAudioAttachmentType; snippet: string }) => void
}

export default function MarkdownAudioPhraseDialog({
  open,
  onOpenChange,
  selectedText,
  hasSelection,
  onInsert,
}: MarkdownAudioPhraseDialogProps) {
  const [attachmentType, setAttachmentType] = React.useState<MarkdownAudioAttachmentType>('phrase')
  const [phrase, setPhrase] = React.useState(selectedText)
  const [hint, setHint] = React.useState('')
  const [audioFile, setAudioFile] = React.useState<File | null>(null)
  const [uploading, setUploading] = React.useState(false)

  React.useEffect(() => {
    if (open) {
      setPhrase(selectedText)
      setHint('')
      setAudioFile(null)
      setAttachmentType(hasSelection ? 'phrase' : 'section')
    }
  }, [open, selectedText, hasSelection])

  const handleInsert = async () => {
    if (attachmentType === 'phrase') {
      const trimmedPhrase = phrase.trim()
      if (!trimmedPhrase) {
        toast({ title: 'Enter the phrase text', variant: 'destructive' })
        return
      }
    }

    if (!audioFile) {
      toast({ title: 'Select an audio file', variant: 'destructive' })
      return
    }

    setUploading(true)
    try {
      const { url } = await uploadService.uploadContentAudio({ audio: audioFile })
      const snippet =
        attachmentType === 'section'
          ? buildSectionAudioHtml({ audioUrl: url })
          : buildAudioPhraseHtml({
              phrase: phrase.trim(),
              audioUrl: url,
              hint: hint.trim() || undefined,
            })

      if (!snippet) {
        toast({ title: 'Unable to build audio snippet', variant: 'destructive' })
        return
      }

      onInsert({ type: attachmentType, snippet })
      onOpenChange(false)
    } catch (error) {
      const err = error as ErrorResponse
      toast({
        title: 'Audio upload failed',
        description: err.response?.data?.message || 'Unable to upload audio file',
        variant: 'destructive',
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Attach audio</DialogTitle>
          <DialogDescription>
            Choose whether this audio is for a highlighted phrase or an inline player on the current section line.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <Label>Audio type</Label>
            <RadioGroup
              value={attachmentType}
              onValueChange={value => setAttachmentType(value as MarkdownAudioAttachmentType)}
              className="gap-3"
            >
              <label
                htmlFor="audio-type-phrase"
                className="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
              >
                <RadioGroupItem id="audio-type-phrase" value="phrase" className="mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none">Phrase audio</p>
                  <p className="text-sm text-muted-foreground">
                    Attach audio to specific text. Select text first or type the phrase below. Readers click the
                    highlighted phrase to listen.
                  </p>
                </div>
              </label>

              <label
                htmlFor="audio-type-section"
                className="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
              >
                <RadioGroupItem id="audio-type-section" value="section" className="mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none">Section audio</p>
                  <p className="text-sm text-muted-foreground">
                    Insert an inline player after the current line, e.g. next to a heading like &quot;Bài mẫu&quot;.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {attachmentType === 'phrase' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="audio-phrase-text">Phrase</Label>
                <Input
                  id="audio-phrase-text"
                  value={phrase}
                  onChange={e => setPhrase(e.target.value)}
                  placeholder="e.g. ask for a temporary leave"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="audio-phrase-hint">Translation / note (optional)</Label>
                <Input
                  id="audio-phrase-hint"
                  value={hint}
                  onChange={e => setHint(e.target.value)}
                  placeholder="e.g. Xin nghỉ tạm thời"
                />
              </div>
            </>
          )}

          {attachmentType === 'section' && (
            <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              Place the cursor on a heading line such as <span className="font-medium">## Bài mẫu</span>, then insert.
              The audio player will appear inline on that same line, next to the heading text.
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="audio-phrase-file">Audio file</Label>
            <Input
              id="audio-phrase-file"
              type="file"
              accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm,audio/mp4,audio/x-m4a,audio/aac,.mp3,.wav,.ogg,.webm,.m4a,.aac"
              onChange={e => setAudioFile(e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted-foreground">MP3, WAV, OGG, WebM, M4A, or AAC up to 15MB.</p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button type="button" onClick={handleInsert} disabled={uploading}>
            {uploading ? <Loader2 className="size-4 animate-spin" /> : <Mic className="size-4" />}
            {uploading ? 'Uploading...' : attachmentType === 'section' ? 'Insert section audio' : 'Insert phrase'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
